// Self-service password change — available to EVERY signed-in role from the topbar
// (owner 2026-09). The endpoint (POST /api/auth/password) already existed and requires the
// CURRENT password, then kills every live session and re-mints this one, so the caller stays
// signed in on this device while any stolen cookie is dropped (see src/http/auth.js).
//
// Rendered as a small modal overlay. There is no modal class in the design file, so the chrome
// is inline — but every colour is a token (web/index.html :root), never a literal, and it is
// dark-theme only like the rest of the panel.

import { useState } from 'react';
import * as api from '../api';
import { Icon, Note } from '../ui';

// The server is authoritative (config.auth.minPasswordLength, default 8); this only gates the
// button so the user is not told "too short" after a round-trip.
const MIN_LEN = 8;

export default function ChangePassword({ onClose }: { onClose: () => void }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const mismatch = confirm.length > 0 && next !== confirm;
  const canSave = !busy && Boolean(current) && next.length >= MIN_LEN && next === confirm;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSave) return;
    setBusy(true); setError('');
    try {
      await api.changePassword(current, next);
      setDone(true);
    } catch (err) {
      // The API answers 401 `invalid_credentials` when the CURRENT password is wrong — which
      // here means exactly that, not a dead session, so say so plainly.
      if (err instanceof api.ApiError && err.code === 'invalid_credentials') {
        setError('Mevcut şifre hatalı.');
      } else {
        setError(err instanceof api.ApiError ? err.message : 'Şifre güncellenemedi.');
      }
    } finally { setBusy(false); }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 50,
        background: 'color-mix(in srgb, var(--bg) 72%, transparent)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div
        onClick={(ev) => ev.stopPropagation()}
        style={{
          width: '100%', maxWidth: 420, background: 'var(--surface)',
          border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.45)',
        }}
      >
        <div className="panel-hd">
          <Icon name="key" />
          <div className="panel-hd-txt">
            <div className="panel-title">Şifre değiştir</div>
            <div className="panel-sub">Değişiklik sonrası diğer oturumlarınız kapatılır.</div>
          </div>
          <button className="iconbtn" type="button" onClick={onClose} title="Kapat">
            <Icon name="close" />
          </button>
        </div>

        {done ? (
          <div className="panel-body">
            <div className="callout good"><Icon name="check_circle" /><span>Şifreniz güncellendi.</span></div>
            <div className="panel-foot">
              <button className="btn pri full" type="button" onClick={onClose}>Kapat</button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit}>
            <div className="panel-body">
              <div className="field">
                <label htmlFor="cp">Mevcut şifre</label>
                <input id="cp" className="inp" type="password" autoComplete="current-password"
                  value={current} onChange={(e) => setCurrent(e.target.value)} />
              </div>
              <div className="field">
                <label htmlFor="np1">Yeni şifre</label>
                <input id="np1" className="inp" type="password" autoComplete="new-password"
                  value={next} onChange={(e) => setNext(e.target.value)} placeholder="en az 8 karakter" />
              </div>
              <div className="field">
                <label htmlFor="np2">Yeni şifre (tekrar)</label>
                <input id="np2" className="inp" type="password" autoComplete="new-password"
                  value={confirm} onChange={(e) => setConfirm(e.target.value)} />
              </div>
              {mismatch ? <Note error>Şifreler eşleşmiyor.</Note> : null}
              {error ? <Note error>{error}</Note> : null}
            </div>
            <div className="panel-foot" style={{ flexDirection: 'row', gap: 10 }}>
              <button className="btn pri" type="submit" disabled={!canSave}>
                <Icon name="check" /><span>{busy ? 'Kaydediliyor…' : 'Şifreyi güncelle'}</span>
              </button>
              <button className="btn ghost" type="button" disabled={busy} onClick={onClose}>Vazgeç</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
